import { useEffect, useMemo, useState } from 'react';
import { Receipt } from 'lucide-react';
import {
  fetchCustomerOpenInvoices,
  fetchCustomerAllInvoices,
  fetchCustomerCredit,
  applyCustomerCredit,
} from '../../lib/invoices/accountsReceivable';
import ReceivePaymentModal from './ReceivePaymentModal';
import BetaPaymentsModal from '../BetaPaymentsModal';
import { supabase } from '../../lib/supabase';

/** Booking select mirroring the Invoices page so BetaPaymentsModal gets the
 *  exact same data shape when opened from the customer Billing tab. */
const BOOKING_INVOICE_SELECT = `
  id,
  booking_number,
  total_amount,
  subtotal,
  tax_amount,
  discount_amount,
  status,
  location,
  notes,
  created_at,
  updated_at,
  end_date,
  customer:customers!bookings_customer_id_fkey(
    id,
    first_name,
    last_name,
    email,
    phone
  ),
  payments:payments_with_stripe_details(*),
  booking_services(
    id,
    service_id,
    name,
    description,
    price,
    tax_rate,
    tax_amount,
    total_amount,
    discount_amount,
    discount_type,
    discount_value,
    compare_at_price,
    duration_minutes,
    start_date,
    end_date,
    vehicle_id,
    add_ons:booking_service_addons(
      id,
      price,
      discount_type,
      discount_value,
      addon:service_addons(id, name, description)
    )
  )
`;

interface CustomerBillingTabProps {
  businessId: string;
  customerId: string;
  customerName: string;
}

const fmtMoney = (cents: number) =>
  '$' + (Math.round(cents) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function formatDate(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function CustomerBillingTab({ businessId, customerId, customerName }: CustomerBillingTabProps) {
  const [invoices, setInvoices] = useState<any[]>([]);
  const [allInvoices, setAllInvoices] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [receiveOpen, setReceiveOpen] = useState(false);
  const [creditCents, setCreditCents] = useState(0);
  const [applyingCredit, setApplyingCredit] = useState(false);
  // Invoice viewer (same BetaPaymentsModal flow as the Invoices page).
  const [standaloneViewId, setStandaloneViewId] = useState<string | null>(null);
  const [viewBooking, setViewBooking] = useState<any | null>(null);
  const [openingInvoiceId, setOpeningInvoiceId] = useState<string | null>(null);

  const handleOpenInvoice = async (inv: any) => {
    if (openingInvoiceId) return;
    if (!inv.booking_id) {
      // Standalone invoice — the modal loads everything from the invoice id.
      setStandaloneViewId(inv.id);
      return;
    }
    setOpeningInvoiceId(inv.id);
    try {
      const { data, error: bookingError } = await supabase
        .from('bookings')
        .select(BOOKING_INVOICE_SELECT)
        .eq('id', inv.booking_id)
        .single();
      if (bookingError || !data) throw bookingError || new Error('Booking not found');
      setViewBooking(data);
    } catch (e) {
      console.error('Could not open invoice:', e);
      setError(e instanceof Error ? e.message : 'Could not open invoice');
    } finally {
      setOpeningInvoiceId(null);
    }
  };

  useEffect(() => {
    if (!businessId || !customerId) return;
    let active = true;
    setLoading(true);
    setError(null);
    Promise.all([
      fetchCustomerOpenInvoices(businessId, customerId),
      fetchCustomerAllInvoices(businessId, customerId),
      fetchCustomerCredit(businessId, customerId),
    ])
      .then(([openRows, allRows, credit]) => {
        if (active) {
          setInvoices(openRows as any[]);
          setAllInvoices(allRows as any[]);
          setCreditCents(credit);
        }
      })
      .catch((e) => { if (active) setError(e instanceof Error ? e.message : 'Failed to load billing'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [businessId, customerId, refreshKey]);

  const totalDue = useMemo(() => invoices.reduce((s, i) => s + (i.amount_due_cents || 0), 0), [invoices]);

  const handleApplyCredit = async () => {
    setApplyingCredit(true);
    try {
      await applyCustomerCredit(businessId, customerId);
      setRefreshKey((k) => k + 1);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to apply credit');
    } finally {
      setApplyingCredit(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Summary */}
      <div className="flex items-center justify-between bg-gray-50 rounded-xl p-4 border border-gray-200/60">
        <div>
          <p className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">Outstanding balance</p>
          <p className="text-2xl font-bold text-gray-900">{fmtMoney(totalDue)}</p>
          <p className="text-[12px] text-gray-500">{invoices.length} open {invoices.length === 1 ? 'invoice' : 'invoices'}</p>
        </div>
        <div className="flex flex-col items-end gap-1.5">
          {invoices.length > 0 && (
            <button
              type="button"
              onClick={() => setReceiveOpen(true)}
              className="px-4 py-2.5 rounded-xl text-white text-sm font-semibold"
              style={{ backgroundColor: '#007AFF' }}
            >
              Receive Payment
            </button>
          )}
        </div>
      </div>

      {/* Available credit */}
      {creditCents > 0 && (
        <div className="flex items-center justify-between bg-blue-50 rounded-xl p-3.5 border border-blue-100">
          <div>
            <p className="text-[12px] font-medium text-blue-700">Credit available</p>
            <p className="text-[18px] font-bold text-blue-700">{fmtMoney(creditCents)}</p>
          </div>
          {invoices.length > 0 && (
            <button
              type="button"
              onClick={handleApplyCredit}
              disabled={applyingCredit}
              className="px-4 py-2 rounded-xl bg-white border border-blue-200 text-blue-700 text-sm font-semibold disabled:opacity-50"
            >
              {applyingCredit ? 'Applying…' : 'Apply Credit'}
            </button>
          )}
        </div>
      )}

      {/* All invoices (open + paid + void) so the full history is findable here */}
      {loading ? (
        <div className="py-12 text-center text-gray-500 text-sm">Loading…</div>
      ) : error ? (
        <div className="py-8 text-center text-red-600 text-sm">{error}</div>
      ) : allInvoices.length === 0 ? (
        <div className="py-12 text-center">
          <Receipt className="w-9 h-9 text-gray-300 mx-auto mb-2" />
          <p className="text-gray-500 text-sm">No invoices for this customer yet.</p>
        </div>
      ) : (
        <div>
          <p className="mb-2 px-1 text-[11px] font-medium text-gray-500 uppercase tracking-wide">
            Invoices ({allInvoices.length})
          </p>
          <div className="bg-white rounded-xl border border-gray-200/60 overflow-hidden divide-y divide-gray-100">
            {allInvoices.map((inv) => {
              const state: string = inv.payment_state || 'unpaid';
              const isVoid = state === 'void';
              const isPaid = state === 'paid';
              const isPartial = state === 'partial';
              const overdue =
                !isVoid && !isPaid && inv.due_at ? new Date(inv.due_at).getTime() < Date.now() : false;
              return (
                <div
                  key={inv.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => void handleOpenInvoice(inv)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      void handleOpenInvoice(inv);
                    }
                  }}
                  className={`flex items-center justify-between gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50 active:bg-gray-100 transition-colors touch-manipulation ${
                    openingInvoiceId === inv.id ? 'opacity-60' : ''
                  }`}
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  <div className="min-w-0">
                    <div className={`text-[14px] font-medium truncate ${isVoid ? 'text-gray-400 line-through' : 'text-gray-900'}`}>
                      Invoice #{inv.invoice_number || inv.booking_number || '—'}
                    </div>
                    <div className={`text-[12px] ${overdue ? 'text-red-600' : 'text-gray-500'}`}>
                      {!isPaid && !isVoid && inv.due_at
                        ? `Due ${formatDate(inv.due_at)}`
                        : formatDate(inv.invoice_date)}
                      {overdue ? ' · overdue' : ''}
                      {!inv.booking_id ? ' · standalone' : ''}
                    </div>
                  </div>
                  <div className="flex items-center gap-2.5 flex-shrink-0">
                    <span
                      className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${
                        isVoid
                          ? 'bg-gray-100 text-gray-500'
                          : isPaid
                            ? 'bg-green-50 text-green-700'
                            : isPartial
                              ? 'bg-amber-50 text-amber-700'
                              : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {isVoid ? 'Void' : isPaid ? 'Paid' : isPartial ? 'Partial' : 'Unpaid'}
                    </span>
                    <div className="text-right">
                      <div className={`text-[14px] font-bold ${isVoid ? 'text-gray-400' : 'text-gray-900'}`}>
                        {isPaid || isVoid ? fmtMoney(inv.total_cents) : fmtMoney(inv.amount_due_cents)}
                      </div>
                      {!isPaid && !isVoid && (
                        <div className="text-[11px] text-gray-400">of {fmtMoney(inv.total_cents)}</div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <ReceivePaymentModal
        isOpen={receiveOpen}
        onClose={() => setReceiveOpen(false)}
        businessId={businessId}
        customerId={customerId}
        customerName={customerName}
        onSuccess={() => setRefreshKey((k) => k + 1)}
      />

      {/* Invoice viewers — identical flows to the Invoices page */}
      {standaloneViewId && (
        <BetaPaymentsModal
          isOpen={!!standaloneViewId}
          onClose={() => setStandaloneViewId(null)}
          invoiceId={standaloneViewId}
          bookingId=""
          bookingNumber={0}
          bookingData={{ total_amount: 0 }}
          onUpdate={() => setRefreshKey((k) => k + 1)}
        />
      )}

      {viewBooking && (
        <BetaPaymentsModal
          isOpen={!!viewBooking}
          onClose={() => setViewBooking(null)}
          bookingId={viewBooking.id}
          bookingNumber={viewBooking.booking_number}
          bookingData={{
            total_amount: viewBooking.total_amount,
            subtotal: viewBooking.subtotal,
            tax_amount: viewBooking.tax_amount,
            discount_amount: viewBooking.discount_amount,
            location: viewBooking.location,
            notes: viewBooking.notes,
            customer: viewBooking.customer,
            payments: viewBooking.payments,
            booking_services: viewBooking.booking_services,
          }}
          booking={viewBooking}
          onUpdate={() => setRefreshKey((k) => k + 1)}
        />
      )}
    </div>
  );
}
